import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Shield, Scale, BadgeCheck, ChevronDown } from "lucide-react";
import { useState } from "react";
import { isPricingLive, FREE_UNTIL_LABEL, PAYWALL_LABEL } from "@/config/pricing";
import { useAuth } from "@/lib/AuthContext";
import { api } from "@/lib/api";
import { startGoogleLogin as goToGoogleLogin } from "@/lib/login";
import usePageMeta from "@/lib/usePageMeta";

const RAZORPAY_SCRIPT_SRC = "https://checkout.razorpay.com/v1/checkout.js";

export default function Pricing() {
  usePageMeta({ title: "Pricing", description: "Explore free, then unlock the Decide plan: all picks with full reasoning, total cost and break-even, scholarship matching and the AI mentor.", path: "/pricing" });
  const navigate = useNavigate();
  const { user } = useAuth();
  const live = isPricingLive();

  // Paid users skip pricing entirely.
  useEffect(() => {
    if (user?.is_paid === true) navigate("/dashboard", { replace: true });
  }, [user, navigate]);

  // ── Razorpay checkout (only used when live) ────────────────────────────
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState("");
  const [scriptReady, setScriptReady] = useState(false);

  // Dynamically load Razorpay checkout.js on mount; tear down on unmount.
  useEffect(() => {
    const existing = document.querySelector(`script[src="${RAZORPAY_SCRIPT_SRC}"]`);
    if (existing) {
      setScriptReady(true);
      return undefined;
    }
    const tag = document.createElement("script");
    tag.src = RAZORPAY_SCRIPT_SRC;
    tag.async = true;
    tag.onload = () => setScriptReady(true);
    tag.onerror = () => setScriptReady(false);
    document.body.appendChild(tag);
    return () => {
      if (tag.parentNode) tag.parentNode.removeChild(tag);
    };
  }, []);

  const startGoogleLogin = () => {
    goToGoogleLogin("/pricing");
  };

  const handleCheckout = async () => {
    // Pre-paywall: keep the original placeholder behaviour.
    if (!live) {
      navigate("/dashboard");
      return;
    }
    if (!user) {
      startGoogleLogin();
      return;
    }
    setCheckoutLoading(true);
    setCheckoutError("");
    try {
      const res = await api.post("/payments/create-order", null, {
        validateStatus: () => true, // we'll inspect status ourselves
      });
      if (res.status < 200 || res.status >= 300) {
        throw new Error(res.data?.detail || "Could not start checkout. Please try again.");
      }
      if (!scriptReady || !window.Razorpay) {
        throw new Error("Payment service hasn't loaded yet. Please refresh and try again.");
      }
      const { order_id, amount, currency, key_id } = res.data;
      const rzp = new window.Razorpay({
        key: key_id,
        amount,
        currency,
        order_id,
        name: "Unimora AI",
        description: "Decide plan — 6 months full access",
        image: "/logo.png",
        prefill: {
          name: user.name || "",
          email: user.email || "",
        },
        theme: { color: "#4F46E5" },
        modal: {
          ondismiss: () => {
            setCheckoutLoading(false);
            setCheckoutError("");
          },
        },
        // Razorpay hands us a signed receipt; the backend verifies it and unlocks the plan
        // immediately (the webhook is only a backup if this tab closes first).
        handler: async (resp) => {
          try {
            const v = await api.post("/payments/verify", {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            }, { validateStatus: () => true });
            if (v.status < 200 || v.status >= 300) {
              throw new Error(v.data?.detail || "Payment received but not confirmed yet. Please contact us with your payment ID.");
            }
            setCheckoutLoading(false);
            // PaymentSuccess refreshes the signed-in user (now is_paid) on mount.
            navigate("/payment-success");
          } catch (e) {
            setCheckoutError(`${e.message} (payment ID: ${resp.razorpay_payment_id})`);
            setCheckoutLoading(false);
          }
        },
      });
      rzp.on("payment.failed", (resp) => {
        const desc = resp?.error?.description || "Payment failed. Please try again.";
        setCheckoutError(desc);
        setCheckoutLoading(false);
      });
      rzp.open();
    } catch (err) {
      setCheckoutError(err?.message || "Something went wrong. Please try again.");
      setCheckoutLoading(false);
    }
  };

  return (
    <div style={{ background: "white", minHeight: "100vh" }}>
      {/* ── EARLY ACCESS BANNER ── */}
      {!live && (
        <div
          style={{
            background: "var(--ind-soft)",
            borderBottom: "1px solid #C7D2FE",
            padding: "12px 24px",
            textAlign: "center",
          }}
        >
          <span style={{ fontSize: "14px", color: "var(--ind)", fontWeight: 500 }}>
            🎉 Full access is free until {FREE_UNTIL_LABEL} — no payment needed.
          </span>
          <span style={{ fontSize: "13px", color: "var(--s500)", marginLeft: "12px" }}>
            Pricing starts {PAYWALL_LABEL}. Make your decision before then.
          </span>
        </div>
      )}

      <div style={{ maxWidth: "960px", margin: "0 auto", padding: "0 24px" }}>
        {/* ── HEADLINE ── */}
        <div style={{ paddingTop: "22px" }}>
          <h1
            style={{
              fontFamily: "var(--ff-display)",
              fontSize: "48px",
              fontWeight: 700,
              letterSpacing: "-0.025em",
              lineHeight: 1.08,
              textAlign: "center",
              margin: 0,
            }}
          >
            <span style={{ color: "var(--s900)" }}>One decision. </span>
            <span style={{ color: "var(--ind)" }}>One payment.</span>
          </h1>
          <p
            style={{
              fontSize: "16px",
              color: "var(--s500)",
              margin: "16px auto 0",
              textAlign: "center",
              lineHeight: 1.6,
              whiteSpace: "nowrap",
            }}
          >
            No subscription. No consultant fees. Pay once, decide with confidence.
          </p>

          {/* ── TRUST BAR ── */}
          <div
            style={{
              marginTop: "28px",
              fontSize: "13px",
              color: "var(--s500)",
              textAlign: "center",
              display: "flex",
              justifyContent: "center",
              flexWrap: "wrap",
              gap: "12px",
            }}
          >
            <span>₹40L–₹2Cr decision at stake</span>
            <span style={{ color: "var(--s300)" }}>|</span>
            <span>Takes under 20 minutes</span>
            <span style={{ color: "var(--s300)" }}>|</span>
            <span>Free to explore · ₹2,999 to decide</span>
          </div>
        </div>

        {/* ── PRICING CARDS ── */}
        <div className="pricing-grid">
          <ExploreCard live={live} navigate={navigate} />
          <DecideCard
            live={live}
            navigate={navigate}
            onCheckout={handleCheckout}
            checkoutLoading={checkoutLoading}
            checkoutError={checkoutError}
          />
        </div>

        {/* ── OBJECTION CRUSHERS ── */}
        <div
          style={{
            marginTop: "56px",
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: "28px",
          }}
          data-testid="pricing-objections"
        >
          <ObjectionCol
            Icon={Shield}
            heading="Not a subscription"
            text="Pay once. Access everything for 6 months. No renewals."
          />
          <ObjectionCol
            Icon={Scale}
            heading="Cheaper than one consultant hour"
            text="Consultants charge ₹5,000–₹50,000. Unimora AI is ₹2,999 total."
          />
          <ObjectionCol
            Icon={BadgeCheck}
            heading="Every number is sourced"
            text="No estimates without a label. Every figure shows where it came from."
          />
        </div>

        {/* ── FAQ ── (below the fold — scroll to see) */}
        <FAQ />
      </div>

      {/* ── FINAL CTA BAND ── (removed) */}

      {/* Local responsive helper for the two-card grid */}
      <style>{`
        .pricing-grid {
          margin-top: 48px;
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 28px;
        }
        @media (max-width: 768px) {
          .pricing-grid { grid-template-columns: 1fr; }
        }
      `}</style>
    </div>
  );
}

// ─── CARD 1: EXPLORE ───────────────────────────────────────────────
function ExploreCard({ live, navigate }) {
  const ctaLabel = live ? "Start for free →" : "Get early access →";
  return (
    <div
      data-testid="pricing-card-explore"
      style={{
        background: "white",
        border: "1px solid var(--s200)",
        borderRadius: "16px",
        padding: "32px",
        boxShadow: "0 1px 3px rgba(15,23,42,0.08)",
      }}
    >
      <p
        style={{
          fontSize: "11px",
          fontWeight: 500,
          letterSpacing: "0.08em",
          color: "var(--s400)",
          textTransform: "uppercase",
          margin: 0,
        }}
      >
        Explore
      </p>
      <div
        style={{
          fontSize: "40px",
          fontWeight: 700,
          color: "var(--s900)",
          fontVariantNumeric: "tabular-nums",
          letterSpacing: "-0.02em",
          marginTop: "8px",
          lineHeight: 1.1,
        }}
      >
        ₹0
      </div>
      <p style={{ fontSize: "13px", color: "var(--s500)", marginTop: "4px" }}>
        Always free
      </p>

      <div style={{ borderTop: "1px solid var(--s200)", margin: "20px 0" }} />

      <button
        type="button"
        onClick={() => navigate("/dashboard")}
        data-testid="pricing-explore-cta"
        style={{
          border: "1px solid var(--ind)",
          color: "var(--ind)",
          background: "white",
          borderRadius: "8px",
          padding: "12px 24px",
          width: "100%",
          fontWeight: 600,
          fontSize: "14px",
          cursor: "pointer",
        }}
      >
        {ctaLabel}
      </button>

      <div style={{ marginTop: "24px" }}>
        <FeatureItem on label="University Atlas — full browse" />
        <FeatureItem on label="Top 3 university matches" />
        <FeatureItem on label="Basic university profiles" />
        <FeatureItem on label="Student profile" />
        <FeatureItem label="Full 10 picks with reasoning" />
        <FeatureItem label="Financial Intelligence" />
        <FeatureItem label="AI Counsel" />
        <FeatureItem label="Final Verdict report" />
        <FeatureItem label="Scholarship Intelligence" />
      </div>
    </div>
  );
}

// ─── CARD 2: DECIDE ────────────────────────────────────────────────
function DecideCard({ live, navigate, onCheckout, checkoutLoading, checkoutError }) {
  const ctaLabel = checkoutLoading ? "Opening checkout…" : live ? "Get full access →" : "Get early access →";
  return (
    <div
      data-testid="pricing-card-decide"
      style={{
        position: "relative",
        background: "white",
        border: "2px solid var(--ind)",
        borderRadius: "16px",
        padding: "32px",
        boxShadow: "0 12px 32px -16px rgba(79,70,229,0.28)",
      }}
    >
      <span
        style={{
          position: "absolute",
          top: "-12px",
          left: "24px",
          background: "var(--ind)",
          color: "white",
          fontSize: "10px",
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          padding: "4px 10px",
          borderRadius: "999px",
          fontWeight: 600,
        }}
      >
        Most Popular
      </span>

      <p
        style={{
          fontSize: "11px",
          fontWeight: 500,
          letterSpacing: "0.08em",
          color: "var(--ind)",
          textTransform: "uppercase",
          margin: 0,
        }}
      >
        Decide
      </p>
      <div
        style={{
          fontSize: "40px",
          fontWeight: 700,
          color: "var(--s900)",
          fontVariantNumeric: "tabular-nums",
          letterSpacing: "-0.02em",
          marginTop: "8px",
          lineHeight: 1.1,
        }}
      >
        ₹2,999
      </div>
      <p style={{ fontSize: "13px", color: "var(--s500)", marginTop: "4px" }}>
        Per family · One-time · One student · 6 months
      </p>

      <div style={{ borderTop: "1px solid var(--s200)", margin: "20px 0" }} />

      <button
        type="button"
        // handleCheckout covers every case: pre-paywall -> dashboard, signed out -> Google
        // sign-in, signed in -> Razorpay. (Previously this always went to /dashboard, so the
        // live paywall could never take a payment.)
        onClick={onCheckout}
        disabled={checkoutLoading}
        data-testid="pricing-decide-cta"
        style={{
          background: "var(--ind)",
          color: "white",
          border: "none",
          borderRadius: "8px",
          padding: "12px 24px",
          width: "100%",
          fontWeight: 600,
          fontSize: "14px",
          cursor: checkoutLoading ? "wait" : "pointer",
          opacity: checkoutLoading ? 0.7 : 1,
        }}
      >
        {ctaLabel}
      </button>
      {checkoutError && (
        <p role="alert" data-testid="pricing-checkout-error"
           style={{ fontSize: "13px", color: "#B91C1C", marginTop: "10px" }}>
          {checkoutError}
        </p>
      )}
      {!live && (
        <p
          style={{
            fontSize: "12px",
            color: "var(--s500)",
            textAlign: "center",
            marginTop: "8px",
          }}
        >
          Free until {FREE_UNTIL_LABEL} — no payment today
        </p>
      )}

      <div style={{ marginTop: "24px" }}>
        <FeatureItem on label="Everything in Explore" />
        <FeatureItem on label="Full Decision Engine — all 10 picks" />
        <FeatureItem on label="16-dimension scoring with reasoning" />
        <FeatureItem on label="Financial Intelligence — total cost + break-even" />
        <FeatureItem on label="Side-by-side compare — up to 4 universities" />
        <FeatureItem on label="Scholarship Intelligence — matched to your profile" />
        <FeatureItem on label="Student Voices — what students actually say" />
        <FeatureItem on label="AI Counsel — unlimited sessions" />
        <FeatureItem on label="Final Verdict — Safe / Target / Reach report" />
        <FeatureItem on label="Visa Intelligence per country" />
        <FeatureItem on label="Valid for 6 months" />
      </div>
    </div>
  );
}

// ─── PRIMITIVES ────────────────────────────────────────────────────
function FeatureItem({ on = false, label }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: "10px",
        padding: "6px 0",
      }}
    >
      <span
        aria-hidden
        style={{
          flexShrink: 0,
          width: "16px",
          marginTop: "1px",
          color: on ? "var(--ind)" : "var(--s300)",
          fontWeight: 700,
        }}
      >
        {on ? "✓" : "—"}
      </span>
      <span
        style={{
          fontSize: "14px",
          color: on ? "var(--s900)" : "var(--s300)",
          textDecoration: on ? "none" : "line-through",
          lineHeight: 1.55,
        }}
      >
        {label}
      </span>
    </div>
  );
}

function ObjectionCol({ Icon, heading, text }) {
  return (
    <div>
      <Icon size={24} strokeWidth={1.8} color="var(--ind)" />
      <div
        style={{
          fontSize: "15px",
          fontWeight: 600,
          color: "var(--s900)",
          marginTop: "14px",
        }}
      >
        {heading}
      </div>
      <p
        style={{
          fontSize: "14px",
          color: "var(--s500)",
          lineHeight: 1.6,
          marginTop: "8px",
        }}
      >
        {text}
      </p>
    </div>
  );
}

function FAQ() {
  const items = [
    {
      q: "Can I use Unimora AI for free forever?",
      a: "The Explore tier is always free. Browse universities, see your top 3 matches, build your profile. The full decision — financial plan, all 10 picks, AI Counsel, Final Verdict — requires the Decide plan.",
    },
    {
      q: 'What does "per family" mean?',
      a: "One payment covers one student's complete decision journey. If you have a second child applying separately, that would be a second purchase.",
    },
    {
      q: "Is this truly a one-time payment?",
      a: "Yes. ₹2,999 once, full access for 6 months. No monthly charges, no auto-renewal, no surprises.",
    },
    {
      q: "What can I use for free?",
      a: "Browse all 80 universities, compare two side by side and see your first three top picks without paying. The Decide plan unlocks the full Decision Engine, Financial Intelligence, AI Counsel and the Final Verdict report.",
    },
    {
      q: "What if I change my mind?",
      a: "You get a full refund within 7 days of paying, as long as you haven't generated a Final Verdict report. Your profile and saved data stay in your account either way. See our refund policy at unimora.in/refund.",
    },
  ];
  const [open, setOpen] = useState(0);
  return (
    <div
      style={{ maxWidth: "640px", margin: "72px auto 64px" }}
      data-testid="pricing-faq"
    >
      {items.map((it, i) => {
        const isOpen = i === open;
        return (
          <div
            key={i}
            style={{ borderBottom: "1px solid var(--s200)" }}
            data-testid={`faq-item-${i}`}
          >
            <button
              type="button"
              onClick={() => setOpen(isOpen ? -1 : i)}
              style={{
                width: "100%",
                background: "transparent",
                border: "none",
                cursor: "pointer",
                padding: "18px 0",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: "16px",
                fontSize: "15px",
                fontWeight: 600,
                color: "var(--s900)",
                textAlign: "left",
              }}
            >
              <span>{it.q}</span>
              <ChevronDown
                size={18}
                strokeWidth={2}
                style={{
                  flexShrink: 0,
                  color: "var(--s400)",
                  transform: isOpen ? "rotate(180deg)" : "rotate(0)",
                  transition: "transform 160ms ease",
                }}
              />
            </button>
            {isOpen && (
              <p
                style={{
                  fontSize: "14px",
                  color: "var(--s500)",
                  lineHeight: 1.6,
                  paddingBottom: "18px",
                  margin: 0,
                }}
              >
                {it.a}
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}
